import { ExecutionStatus, ExecutionType, ReturnWhen } from "graphql/generated/graphql";
import { CSSProperties } from "react";
import { Node } from "reactflow";
import { Direction, getPositionedGraph } from "../visualization.service";

const defaultNodeWidth = 222;
const defaultNodeHeight = 48;
// const parentNodePaddingInline = 26;
// const parentNodePaddingBlock = 26;
const interNodeSpacing = 30;

export interface ProcessState {
    nodes: ProcessNode[];
    edges: ProcessEdge[];
    resources: ProcessResource[];
}

export interface ProcessNode {
    id: string;
    originalId: string;
    type: ExecutionType;
    created_at?: string;
    data: ProcessDescription;
    resources: ProcessResource[]; // TODO this is redundant, this should be ids only
}

interface ProcessDescription {
    name: string;
    status?: ExecutionStatus;
    is_deleted?: boolean;
    fading_out?: boolean;
    started_at?: string;
    estimate_p75_seconds?: number;
}

/**
 * The relationship KIND an edge represents, as tagged by the backend
 * (/execution/{id}/process/visualization). An execution pair can be related by the
 * structural spawn tree (parent_execution_id), by the dependency graph
 * (dependency/dependency_execution), or by BOTH.
 */
export type EdgeKind = "parent_child" | "dependency" | "both";

/**
 * Colour palette for the three edge kinds. Kept in sync with the SCSS theme palette
 * (assets/scss/abstracts/variables/_colors.scss). Colour encodes the relationship KIND
 * and is orthogonal to the reactflow `animated` cue, which stays reserved for the
 * "not-yet-ended / active" state.
 */
export const edgeKindColors: Record<EdgeKind, string> = {
    parent_child: "#0e86d1", // secondary-blue — structural spawn (parent → child)
    dependency: "#f2b100", // primary-yellow — wait/ordering dependency
    both: "#2e8c00", // primary-green — spawned AND awaited
};

export const edgeKindLabels: Record<EdgeKind, string> = {
    parent_child: "Child execution",
    dependency: "Dependency",
    both: "Child & dependency",
};

/** Base stroke style for an edge given its kind. Empty for an unknown/absent kind. */
export const getEdgeKindStyle = (kind?: EdgeKind): CSSProperties =>
    kind != null && kind in edgeKindColors
        ? { stroke: edgeKindColors[kind], strokeWidth: 1.5 }
        : {};

/**
 * Aggregate the per-execution relationship KIND from the backend-tagged edges.
 *
 * Returns a map from execution id (`ProcessNode.originalId`) to the `EdgeKind` that
 * summarises how that execution relates to the rest of the tree. Edge direction is
 * `source = child/waiter`, `target = parent/waitee` (see `sortNodesByCreatedAt`), so a
 * node's OWN upstream relationships are the edges where it is the `source`: whether it is
 * a spawned child (`parent_child`), a dependency waiter (`dependency`), or `both`.
 *
 * Powers the Children-tab "Relation" column, which reuses the same palette
 * (`edgeKindColors` / `edgeKindLabels`) as the graph edges + legend. Nodes with no
 * tagged source edge (e.g. the root execution) are absent from the map (column shows —).
 */
export const buildExecutionKindMap = (
    nodes: ProcessNode[] = [],
    edges: ProcessEdge[] = []
): Record<string, EdgeKind> => {
    const originalIdByNodeId = new Map<string, string>();
    for (const node of nodes) {
        originalIdByNodeId.set(node.id, node.originalId);
    }
    const flags = new Map<string, { child: boolean; dependency: boolean }>();
    for (const edge of edges) {
        if (edge.kind == null) continue;
        const executionId = originalIdByNodeId.get(edge.source);
        if (executionId == null) continue;
        const entry = flags.get(executionId) ?? { child: false, dependency: false };
        if (edge.kind === "parent_child" || edge.kind === "both") entry.child = true;
        if (edge.kind === "dependency" || edge.kind === "both") entry.dependency = true;
        flags.set(executionId, entry);
    }
    const kindMap: Record<string, EdgeKind> = {};
    for (const [executionId, { child, dependency }] of flags) {
        kindMap[executionId] =
            child && dependency ? "both" : child ? "parent_child" : "dependency";
    }
    return kindMap;
};

export interface ProcessEdge {
    id: string;
    type?: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
    return_when: ReturnWhen;
    kind?: EdgeKind;
    style: CSSProperties;
    animated?: boolean;
}

interface ProcessResource {
    id: string;
    type:
        | "connector"
        | "schedule"
        | "webhook"
        | "setting"
        | "message"
        | "flow"
        | "wrapper"
        | "sync_config"
        | "plugin_action";
    name: string;
}

const compareCreatedAtById = (
    aId: string,
    bId: string,
    createdAtById: Map<string, string | undefined>
): number => {
    const aTime =
        createdAtById.get(aId) != null ? Date.parse(createdAtById.get(aId) as string) : 0;
    const bTime =
        createdAtById.get(bId) != null ? Date.parse(createdAtById.get(bId) as string) : 0;
    if (aTime !== bTime) {
        return aTime - bTime;
    }
    return aId.localeCompare(bId);
};

const compareCreatedAt = (a: ProcessNode, b: ProcessNode): number => {
    const createdAtById = new Map([
        [a.id, a.created_at],
        [b.id, b.created_at],
    ]);
    return compareCreatedAtById(a.id, b.id, createdAtById);
};

/**
 * Dagre may reorder siblings while minimizing crossings, and it may interleave the
 * children of DIFFERENT parents on the rank axis (e.g. a later parent's earlier-created
 * child ends up between an earlier parent's children), which produces crossing edges and
 * overlapping groups.
 *
 * Reassign rank-axis positions so that, at every depth: nodes are grouped by their parent
 * (siblings kept contiguous), parent groups are ordered by their parent's already-corrected
 * rank position, and siblings within a group are ordered by created_at. Depths are processed
 * from the root outward, so each level can rely on its parents' corrected positions — which
 * makes the grouping recursive across ALL generations, not just gen-2. The set of rank slots
 * occupied at each depth is preserved, so ranks and the overall layout footprint are unchanged.
 *
 * Expects layout edges with source = parent and target = child.
 */
export const reorderSiblingPositionsByCreatedAt = (
    positionedNodes: Node[],
    layoutEdges: ProcessEdge[],
    direction: Direction
): Node[] => {
    if (positionedNodes.length <= 1 || layoutEdges.length === 0) {
        return positionedNodes;
    }

    const nodesById = new Map(
        positionedNodes.map((node) => [node.id, { ...node, position: { ...node.position } }])
    );
    const createdAtById = new Map(
        positionedNodes.map((node) => [node.id, (node as ProcessNode).created_at])
    );

    // child -> parent. A node may have several incoming edges in a DAG; the first wins,
    // which is enough to keep each node in a single, contiguous sibling group.
    const parentByChild = new Map<string, string>();
    for (const edge of layoutEdges) {
        if (!parentByChild.has(edge.target)) {
            parentByChild.set(edge.target, edge.source);
        }
    }

    const rankAxis = direction === Direction.Horizontal ? "y" : "x";
    const depthAxis = direction === Direction.Horizontal ? "x" : "y";

    const rankPositionOf = (id: string): number => nodesById.get(id)?.position[rankAxis] ?? 0;

    // Group node ids by depth (dagre rank). Process nodes are uniformly sized, so every node
    // in a rank shares the same depth-axis coordinate; rounding absorbs float noise.
    const idsByDepth = new Map<number, string[]>();
    for (const node of nodesById.values()) {
        const depth = Math.round(node.position[depthAxis]);
        const ids = idsByDepth.get(depth) ?? [];
        ids.push(node.id);
        idsByDepth.set(depth, ids);
    }

    // Shallow -> deep, so a level's parents are already corrected before we order the level.
    const depths = [...idsByDepth.keys()].sort((a, b) => a - b);
    for (const depth of depths) {
        const ids = idsByDepth.get(depth) as string[];
        if (ids.length <= 1) {
            continue;
        }
        // The rank slots this level occupies are preserved as a set and only re-assigned.
        const slots = ids.map((id) => rankPositionOf(id)).sort((a, b) => a - b);
        const ordered = [...ids].sort((a, b) => {
            const parentA = parentByChild.get(a);
            const parentB = parentByChild.get(b);
            const parentPosA = parentA != null ? rankPositionOf(parentA) : -Infinity;
            const parentPosB = parentB != null ? rankPositionOf(parentB) : -Infinity;
            // Primary: order groups by their parent's (already-corrected) rank position.
            if (parentPosA !== parentPosB) {
                return parentPosA - parentPosB;
            }
            // Distinct parents that coincidentally share a rank position: keep each group
            // contiguous by tie-breaking on parent id.
            if (parentA !== parentB) {
                return (parentA ?? "").localeCompare(parentB ?? "");
            }
            // Same parent: order siblings by created_at.
            return compareCreatedAtById(a, b, createdAtById);
        });
        ordered.forEach((id, index) => {
            const node = nodesById.get(id);
            if (node == null) {
                return;
            }
            node.position = {
                ...node.position,
                [rankAxis]: slots[index],
            };
        });
    }

    return positionedNodes.map((node) => nodesById.get(node.id) ?? node);
};

/**
 * Sort nodes in tree order with siblings ordered by created_at.
 * Edge direction: source = child (waiter), target = parent (waitee).
 */
export const sortNodesByCreatedAt = (
    nodes: ProcessNode[],
    edges: ProcessEdge[]
): ProcessNode[] => {
    if (nodes.length <= 1) {
        return nodes;
    }

    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const childrenByParent = new Map<string, ProcessNode[]>(
        nodes.map((node) => [node.id, []])
    );

    for (const edge of edges) {
        const parent = nodeById.get(edge.target);
        const child = nodeById.get(edge.source);
        if (parent == null || child == null) {
            continue;
        }
        childrenByParent.get(parent.id)?.push(child);
    }

    for (const children of childrenByParent.values()) {
        children.sort(compareCreatedAt);
    }

    const childIds = new Set(edges.map((edge) => edge.source));
    const roots = nodes.filter((node) => !childIds.has(node.id)).sort(compareCreatedAt);

    const sorted: ProcessNode[] = [];
    const visited = new Set<string>();

    const visit = (node: ProcessNode) => {
        if (visited.has(node.id)) {
            return;
        }
        visited.add(node.id);
        sorted.push(node);
        for (const child of childrenByParent.get(node.id) ?? []) {
            visit(child);
        }
    };

    for (const root of roots) {
        visit(root);
    }

    for (const node of nodes) {
        if (!visited.has(node.id)) {
            sorted.push(node);
        }
    }

    return sorted;
};

/** get a list of nodes and edges -> return list of positioned nodes
 the nodes are sorted in a particular, basically chronological order
*/
export const getPositionedNodes = (
    nodes: ProcessNode[],
    edges: ProcessEdge[],
    direction: Direction
): Node[] => {
    const layoutEdges = flipEdges(edges);
    const [positionedNodes] = getPositionedGraph(
        transformToReactFlowProcessNode(nodes).map((node) => {
            return {
                width: defaultNodeWidth,
                height: defaultNodeHeight,
                ...node,
                data: { layoutDirection: direction, ...node.data },
            };
        }),
        layoutEdges,
        0,
        0,
        direction,
        interNodeSpacing
    );

    return reorderSiblingPositionsByCreatedAt(positionedNodes, layoutEdges, direction);
};

export const transferOrderToIds = (
    nodes: ProcessNode[],
    edges: ProcessEdge[]
): [nodes: ProcessNode[], edges: ProcessEdge[]] => {
    let newEdges = [...edges];
    const indexPadWidth = Math.max(1, String(Math.max(0, nodes.length - 1)).length);

    const newNodes = nodes.map((node, index) => {
        const newNodeId = `${String(index).padStart(indexPadWidth, "0")}.${node.id}`;
        newEdges = newEdges.map((edge) => {
            return {
                ...edge,
                source: edge.source.replace(node.id, newNodeId),
                target: edge.target.replace(node.id, newNodeId),
            };
        });
        return {
            ...node,
            id: newNodeId,
            originalId: node.id,
        };
    });

    return [newNodes, newEdges];
};

export const getPositionedResourceNodes = (resources: ProcessResource[]): Node[] => {
    const nodes = transformToReactFlowResourceNode(resources);
    const [positionedNodes] = getPositionedGraph(
        nodes,
        [],
        0,
        0,
        // note that this is wrt to connected nodes, but here we use
        // unconnected nodes -> inverted
        Direction.Horizontal,
        interNodeSpacing
    );

    return positionedNodes;
};

export const transformToReactFlowResourceNode = (resource: ProcessResource[]) =>
    resource.map((resource) => {
        return {
            id: resource.id,
            type: resource.type,
            data: {
                name: resource.name,
            },
            position: { x: 0, y: 0 },
            width: defaultNodeWidth,
            height: defaultNodeHeight,
        };
    });

export const transformToReactFlowProcessNode = (nodes: ProcessNode[]) =>
    nodes.map((node) => {
        return {
            ...node,
            position: { x: 0, y: 0 },
        };
    });

export const flipEdges = (edges: Array<ProcessEdge>): Array<ProcessEdge> => {
    return edges.map((edge) => {
        return {
            ...edge,
            source: edge.target,
            target: edge.source,
        };
    });
};

/**
 * Given a list of nodes and edges, determines the set of nodes that belong to the parent execution.
 * @param isChildExecution
 * @param nodes
 * @param edges
 */
export const getParentNodes = (
    isChildExecution: boolean,
    nodes: ProcessNode[],
    edges: ProcessEdge[]
): ProcessNode[] => {
    if (!isChildExecution) return [];
    // pick all nodes that are not used as a source/that do not have an "outgoing" edge
    return nodes.filter((node) => !edges.some((edge) => edge.source === node.id));
};

/**
 * Given a list of parent nodes and edges, determines the set of edges that connect a parent node.
 * @param isChildExecution
 * @param nodes
 * @param edges
 */
export const getParentEdges = (
    isChildExecution: boolean,
    parentNodes: ProcessNode[],
    edges: ProcessEdge[]
): ProcessEdge[] => {
    if (!isChildExecution) return [];
    // pick all edges that are inbound for parent nodes
    // => ie. for which edge.target is a parent node
    // (by definition, there shouldn't be any outgoing edges)
    return edges.filter((edge) => parentNodes.some((parentNode) => parentNode.id === edge.target));
};

const activeStates: ExecutionStatus[] = [
    "AT_ENGINE",
    "PAUSED",
    "SCHEDULED",
    "WAITING_DEPENDENCY",
    "WAITING_ENGINE",
    "WAITING_LOCK",
    "WAITING_RECORD",
    "WAITING_RESPONSE",
];
/**
 * Given a list of nodes and edges, determines the set of edges that are associated with active nodes.
 * @param nodes
 * @param edges
 */
export const getActiveEdges = (nodes: ProcessNode[], edges: ProcessEdge[]): ProcessEdge[] => {
    const activeNodes = nodes.filter((node) =>
        node.data.status == null ? true : activeStates.includes(node.data.status)
    );
    // get all edges that emerge from an active edge
    // => ie. all edges which have an active node as a source
    return edges.filter((edge) => activeNodes.some((activeNode) => activeNode.id === edge.source));
};
